import {
  Component, ElementRef, OnDestroy, OnInit, ViewChild, inject,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Ability } from '@casl/ability';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { dailyDiarySubject } from '../daily-diary-ability.util';
import {
  hasRemarkLocation,
  normalizeLocationPayload,
  projectHasAlignmentGeometry,
  readRemarkLocation,
  remarkLocationPayload,
  resolveDailyReportLocationMode,
} from '../daily-diary-remark.util';
import * as moment from 'moment';

import { ProjectsService } from '../../../../../core/projects.service';
import { CommonService } from '../../../../../../../shared/services/common.service';
import {
  ConfirmDialogComponent, ConfirmDialogModel,
} from '../../../../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { UploadFileRequest } from '../../../../../../../shared/core/shared.actions';
import { dailyReportLocationConfig } from '../../../../../../../shared/core/shared.selectors';
import { currentLocation, projectDetails, siteDetails } from '../../../../../core/projects.selectors';
import { LocationPickerLauncherService } from '../../../../../../../shared/services/location-picker-launcher.service';
import { clearLocationControls, linkRawValue } from '../../../../../../../shared/helpers/design-context';
import { DrawingsAvailabilityService } from '../../../../../../../shared/services/drawings-availability.service';
import { DrawingsAnalytics } from '../../../../projects-details-page/components/tab-screens/drawings/drawings-analytics';
import { SheetMarkupQueueService } from '../../../../../../../shared/services/sheet-markup-queue.service';
import { MapFeatureLink } from '../../../../../../../shared/models/map-feature-link';
import { DailyReportService } from '../../daily-report/daily-report.service';
import { AuthService } from '../../../../../../../shared/services/auth.service';
import {
  baseUpdatedAtFor,
  openReportChangedDialog,
  ReportChangedBody,
  reportChangedBody,
} from '../../../../../../../shared/helpers/report-conflict';
import {
  StationRangeComponent,
} from '../../daily-report/daily-report-components/detail-components/control-forms-details/station-range/station-range.component';

interface RemarkEditModel {
  uuid: string;
  id?: string;
  type: string;
  title: string;
  description: string;
  pictures: any[]; // array of attachment objects { id, fileType, url, ... }
  // Same optional location fields as a report activity (see daily-diary-remark.util).
  latitude?: any;
  longitude?: any;
  location?: string;
  station_range?: any;
  map_feature_link?: MapFeatureLink | null;
}

interface DropdownOption {
  value: string;
  label: string;
}

/**
 * Which slice of the diary this panel edits.
 *
 * Each value is one component section in the main body. A panel renders only
 * its own fields but still loads and saves the whole diary record, so saving
 * weather can never drop remarks.
 *
 * 'remark' is deliberately SINGULAR. The daily report opens one entry per
 * panel — its Remarks section lists summary cards and clicking one opens a
 * panel titled "Remark" holding just that entry — so the diary does the same
 * rather than stacking every remark into a single editor.
 *
 * There is no whole-record variant: a diary is created from a small dialog
 * holding the day-level fields (CreateDailyDiaryComponent), the same way a
 * daily report is, and everything else is filled in section by section.
 */
export type DailyDiarySection = 'workday' | 'weather' | 'remark';

const SECTION_TITLES: Record<DailyDiarySection, string> = {
  workday: 'Work day',
  weather: 'Weather',
  remark: 'Remark',
};

@Component({
  selector: 'app-daily-diary-form',
  templateUrl: './daily-diary-form.component.html',
  styleUrls: ['./daily-diary-form.component.scss'],
  standalone: false,
})
export class DailyDiaryFormComponent implements OnInit, OnDestroy {
  // { projectId, siteId, mode: 'edit'|'view', diaryId: string,
  //   section: DailyDiarySection, remarkUuid?: string }
  data: any;
  options: any;

  @ViewChild('fileInput', { static: false }) fileInputRef!: ElementRef<HTMLInputElement>;

  diary: any = null;
  diaryId: string | null = null;
  mode: 'edit' | 'view' = 'view';
  section: DailyDiarySection = 'workday';

  /** Which remark the single-remark panel is editing (see DailyDiarySection). */
  remarkUuid: string | null = null;

  /** Position held by the edited remark across a save — see resolveActiveRemark. */
  private pendingRemarkIndex = -1;

  /** Set when the save in flight should close the panel once it lands. */
  private pendingClose = false;

  loading = false;
  saving = false;

  form: FormGroup;
  remarkCategories: string[] = [];
  remarkCategoryOptions: DropdownOption[] = [];

  uploadTargetRemarkUuid: string | null = null;

  /**
   * Whether the "+" menu offers "Station range": the report toolbar's rule
   * (resolved daily-report location mode — project, then owner org, then app
   * config — is `station_range`) OR, as mobile does, the project simply has
   * alignments to pick stations on. Alaska DOT projects set no location mode
   * anywhere but do carry `alignment_geometry`.
   */
  stationRangeEnabled = false;

  private project: any = null;
  private locationConfig: any = null;

  private readonly onDestroy: Subject<any> = new Subject<any>();
  /** Picker strokes held for a remark's sheet link, posted once the diary save returns the link. */
  private readonly sheetMarkups = inject(SheetMarkupQueueService);
  private readonly drawingsAvailability = inject(DrawingsAvailabilityService);
  private readonly track = inject(DrawingsAnalytics);
  /** W49: "Mark on drawing" in the remark's ＋ menu */
  hasPublishedSheets = false;

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private dialog: MatDialog,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private ability: Ability,
    private dailyReportService: DailyReportService,
    private locationPickerLauncher: LocationPickerLauncherService,
    private authService: AuthService,
  ) {
    this.form = this.fb.group({
      // The shared `app-date-picker` is backed by the moment date adapter, so
      // this control holds a moment (not the api's YYYY-MM-DD string).
      // buildPayload() formats it back on the way out.
      diary_date: [moment(), Validators.required],
      contractor_working: [true],
      weather: this.fb.group({
        avgtemp: [null],
        maxtemp: [null],
        mintemp: [null],
        wind: [null],
        condition_text: [null],
        note: [null],
      }),
      // Remarks used to be a plain array driven by [(ngModel)] + standalone:true,
      // which meant they were not form controls at all and could not use the
      // shared form-field components. They are a FormArray of FormGroups now.
      remarks: this.fb.array([]),
    });
  }

  get weatherGroup(): FormGroup {
    return this.form.get('weather') as FormGroup;
  }

  get remarks(): FormArray {
    return this.form.get('remarks') as FormArray;
  }

  remarkGroup(index: number): FormGroup {
    return this.remarks.at(index) as FormGroup;
  }

  /** The one-entry-per-panel editor, matching the report's "Remark" panel. */
  get isSingleRemark(): boolean {
    return this.section === 'remark';
  }

  get panelTitle(): string {
    return SECTION_TITLES[this.section];
  }

  /** A panel renders only its own block. */
  showsSection(section: DailyDiarySection): boolean {
    return this.section === section;
  }

  get activeRemarkIndex(): number {
    return (this.remarks.controls as FormGroup[])
      .findIndex(group => group.get('uuid')?.value === this.remarkUuid);
  }

  /** null until the diary has loaded and the target remark has been resolved. */
  get activeRemarkGroup(): FormGroup | null {
    const index = this.activeRemarkIndex;
    return index < 0 ? null : this.remarkGroup(index);
  }

  ngOnInit(): void {
    if (this.data?.projectId) {
      this.drawingsAvailability.available(this.data.projectId).then(found => (this.hasPublishedSheets = found)).catch(() => undefined);
    }
    this.mode = this.data?.mode || 'view';
    this.section = this.data?.section || 'workday';
    this.diaryId = this.data?.diaryId || null;
    this.remarkUuid = this.data?.remarkUuid || null;

    // Pull remark category list from the server (don't hardcode).
    this.projectsService.fetchDailyDiaryRemarkCategories()
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (resp: any) => {
          const list = resp?.data || resp || [];
          // The endpoint returns objects ({id, name, value, source_entity_id}),
          // not strings. Binding them straight through rendered every menu
          // item as "[object Object]" and stored the object as the remark's
          // `type`. Take the display string.
          this.remarkCategories = Array.isArray(list)
            ? list
              .map((cat: any) => (typeof cat === 'string' ? cat : cat?.name ?? cat?.value ?? ''))
              .filter((name: string) => !!name)
            : [];
          this.remarkCategoryOptions = this.buildCategoryOptions();
          // The categories request races the diary load, so a remark seeded
          // before it landed has an empty type. Backfill it rather than let
          // the save fail server-side with "Invalid remark type: ".
          this.backfillRemarkTypes();
        },
        error: () => {
          this.remarkCategories = [];
          this.remarkCategoryOptions = [];
        },
      });

    if (this.diaryId) {
      this.loadDiary(this.diaryId);
    }

    // Listen for uploaded files. Attach the file to whichever remark started
    // the upload (`uploadTargetRemarkUuid`).
    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        if (!this.uploadTargetRemarkUuid) return;
        const items = JSON.parse(JSON.stringify(Array.isArray(data) ? data : [data]));
        const newFiles = items.filter(
          (item: any) => item?.fileType === 'picture' || item?.fileType === 'document',
        );
        if (!newFiles.length) return;
        const remark = this.findRemarkGroup(this.uploadTargetRemarkUuid);
        if (remark) {
          const pictures = remark.get('pictures');
          pictures?.setValue([...(pictures.value || []), ...newFiles]);
          this.form.markAsDirty();
        }
        this.uploadTargetRemarkUuid = null;
      });

    // The pin picker and the station dialog both hand their result to this
    // shared subject (the station dialog ONLY does — it closes with no
    // result), and so does `app-location-station-card` when the card itself
    // is clicked to edit. Route whatever arrives into the remark being edited.
    // Nothing else that listens on it can be open while this panel is: it is
    // the report's per-entry editors, which live in the same right panel.
    this.dailyReportService.selectedLocation
      .pipe(takeUntil(this.onDestroy))
      .subscribe(location => this.applyPickedLocation(location));

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.project = project;
        this.stationRangeEnabled = this.resolveStationRangeEnabled();
      });
    this.store.select(dailyReportLocationConfig)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(config => {
        this.locationConfig = config;
        this.stationRangeEnabled = this.resolveStationRangeEnabled();
      });

    // Publish edit state to the shared flag the diary's date strip and section
    // headers check before they navigate away. Nothing was setting it to true,
    // so their "unsaved changes" prompt could never fire and switching day or
    // section silently dropped whatever had been typed.
    this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.projectsService.isAnyFormDirty.next(this.form.dirty));

    // The date is fixed once the diary exists. `addDraft`'s update path writes
    // only weather, contractor_working and updated_by — `diary_date` is
    // ignored — so an editable field here promised a change it never made and
    // still reported "Diary saved". It is chosen in the create dialog.
    this.form.get('diary_date')?.disable({ emitEvent: false });

    // Disable form on view mode.
    if (this.mode === 'view') {
      this.form.disable({ emitEvent: false });
    }
  }

  loadDiary(diaryId: string): void {
    this.loading = true;
    this.projectsService.fetchDailyDiary({ diaryId })
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (resp: any) => {
          this.loading = false;
          const node = resp?.data || resp;
          const diary = node?.attributes ? { id: node.id, ...node.attributes } : node;
          this.diary = diary;
          this.applyAbilityGates(diary);
          this.patchFormFromDiary(diary);
        },
        error: () => {
          this.loading = false;
          this.commonService.errorNotification('Failed to load diary');
        },
      });
  }

  applyAbilityGates(diary: any): void {
    const subj = dailyDiarySubject(diary);
    const canUpdate = this.ability.can('update', subj);
    if (this.mode === 'edit' && (!canUpdate || diary?.status !== 'draft')) {
      this.mode = 'view';
      this.form.disable({ emitEvent: false });
    }
  }

  patchFormFromDiary(diary: any): void {
    this.form.patchValue({
      diary_date: diary?.diary_date ? moment(diary.diary_date, 'YYYY-MM-DD') : moment(),
      contractor_working: diary?.contractor_working ?? true,
      weather: {
        avgtemp: diary?.weather?.avgtemp ?? null,
        maxtemp: diary?.weather?.maxtemp ?? null,
        mintemp: diary?.weather?.mintemp ?? null,
        wind: diary?.weather?.wind ?? null,
        condition_text: diary?.weather?.condition?.text ?? diary?.weather?.condition_text ?? null,
        note: diary?.weather?.note ?? null,
      },
    }, { emitEvent: false });

    this.remarks.clear({ emitEvent: false });
    (diary?.remarks || []).forEach((r: any) => {
      this.remarks.push(this.buildRemarkGroup({
        // The api's `reconcileRemarks` matches an incoming remark by `uuid`
        // (its `id` comparison is against the numeric column, which the client
        // never sees). The serializer emits the row's 22-char uuid AS `id` and
        // sends no separate `uuid` field, so seed the control from `id` —
        // otherwise every save deleted the remark and inserted a copy, losing
        // its identity, attachments and AASHTOWare sync key.
        uuid: this.serverUuid(r.uuid) || this.serverUuid(r.id) || uuidv4(),
        id: r.id,
        type: r.type || '',
        title: r.title || '',
        description: r.description || '',
        pictures: (r.pictures || []).map((p: any) => ({
          ...p,
          fileType: p?.fileType || 'picture',
        })),
        ...readRemarkLocation(r),
      }), { emitEvent: false });
    });

    // Not while a delete-then-close is in flight: the panel is on its way out
    // and seeding it with a blank remark would flash an empty editor.
    if (this.isSingleRemark && !this.pendingClose) {
      this.resolveActiveRemark();
    }

    // Groups pushed into an already-disabled FormArray come back enabled, which
    // would leave a read-only diary with editable remark fields.
    if (!this.editable()) {
      this.form.disable({ emitEvent: false });
    }

    // Freshly loaded state is not user input. Clear the shared flag too:
    // rebuilding the FormArray above emits valueChanges, and that runs before
    // this line.
    this.form.markAsPristine();
    this.projectsService.isAnyFormDirty.next(false);
  }

  /**
   * Point the single-remark panel at a remark in the freshly rebuilt FormArray.
   *
   * Every load and every save rebuilds the array from the server response, so
   * a remark the panel was editing comes back under its SERVER uuid. Falling
   * back to the position it held keeps the panel on the same entry across the
   * save that first persists it (a new remark is appended, so its index holds).
   * With nothing to fall back to, the panel was opened to add one.
   */
  private resolveActiveRemark(): void {
    if (this.findRemarkGroup(this.remarkUuid)) return;

    const fallback = this.pendingRemarkIndex >= 0
      ? (this.remarks.at(this.pendingRemarkIndex) as FormGroup | null)
      : null;
    if (fallback) {
      this.remarkUuid = fallback.get('uuid')?.value ?? null;
      return;
    }

    if (!this.editable()) return;
    this.addRemark(undefined, false);
    this.remarkUuid = this.remarks.at(this.remarks.length - 1)?.get('uuid')?.value ?? null;
  }

  private buildRemarkGroup(remark: RemarkEditModel): FormGroup {
    return this.fb.group({
      uuid: [remark.uuid],
      id: [remark.id ?? null],
      type: [remark.type],
      title: [remark.title],
      description: [remark.description],
      pictures: [remark.pictures || []],
      latitude: [remark.latitude ?? ''],
      longitude: [remark.longitude ?? ''],
      location: [remark.location ?? ''],
      station_range: [remark.station_range ?? null],
      // Design context: the serialized link as served; buildPayload converts it to the api input shape.
      map_feature_link: [remark.map_feature_link ?? null],
    });
  }

  private findRemarkGroup(uuid: string | null): FormGroup | null {
    if (!uuid) return null;
    return (this.remarks.controls as FormGroup[])
      .find(group => group.get('uuid')?.value === uuid) || null;
  }

  /** Give any type-less remark the default category once we know it. */
  private backfillRemarkTypes(): void {
    const fallback = this.remarkCategories[0];
    if (!fallback) return;
    (this.remarks.controls as FormGroup[]).forEach(group => {
      const control = group.get('type');
      if (!control?.value) control?.setValue(fallback, { emitEvent: false });
    });
  }

  /**
   * The api rejects a remark with no body text (`Joi` requires `title`) or an
   * unknown type (`reconcileRemarks` throws). Both are reachable here: the
   * panel seeds a blank remark on open, and attaching a picture marks the form
   * dirty, which used to be enough to arm Save and then fail with a 400.
   */
  get activeRemarkIncomplete(): boolean {
    if (!this.isSingleRemark) return false;
    const group = this.activeRemarkGroup;
    if (!group) return true;
    return !this.trimmed(group, 'title') || !this.trimmed(group, 'type');
  }

  /** Shown against Detail once the user has touched the panel. */
  get remarkDetailError(): string {
    if (!this.isSingleRemark || !this.form.dirty) return '';
    const group = this.activeRemarkGroup;
    if (group && !this.trimmed(group, 'title')) return 'Detail is required.';
    return '';
  }

  private trimmed(group: FormGroup, control: string): string {
    return (group.get(control)?.value ?? '').toString().trim();
  }

  private buildCategoryOptions(): DropdownOption[] {
    return this.remarkCategories.map(cat => ({ value: cat, label: cat }));
  }

  /**
   * Options for the REMARK TYPE select. The select is always rendered (read
   * only on a submitted diary, like ACTIVITY TYPE on the report), so a stored
   * type that is no longer in the category list — or one shown before the
   * categories request lands — is added as an option, else the disabled
   * select would show nothing for it.
   */
  get activeRemarkTypeOptions(): DropdownOption[] {
    const current = (this.activeRemarkGroup?.get('type')?.value ?? '').toString();
    if (!current || this.remarkCategoryOptions.some(opt => opt.value === current)) {
      return this.remarkCategoryOptions;
    }
    // Memoised: a fresh array on every change-detection pass would re-run the
    // dropdown's ngOnChanges each cycle.
    const memo = this.typeOptionsMemo;
    if (memo?.base !== this.remarkCategoryOptions || memo?.current !== current) {
      this.typeOptionsMemo = {
        base: this.remarkCategoryOptions,
        current,
        merged: [...this.remarkCategoryOptions, { value: current, label: current }],
      };
    }
    return this.typeOptionsMemo!.merged;
  }

  private typeOptionsMemo: { base: DropdownOption[]; current: string; merged: DropdownOption[] } | null = null;

  addRemark(category?: string, markDirty = true): void {
    if (!this.editable()) return;
    this.remarks.push(this.buildRemarkGroup({
      uuid: uuidv4(),
      type: category || this.remarkCategories[0] || '',
      title: '',
      description: '',
      pictures: [],
    }));
    // Seeding the panel with an empty remark to fill in is not user input, so
    // it must not arm the Save button or the unsaved-changes guard.
    if (markDirty) this.form.markAsDirty();
  }

  /**
   * The `...` menu's "Delete". Drops the remark and saves straight away, so the
   * panel it was opened from doesn't linger over an entry that no longer
   * exists. An entry never persisted just disappears with the panel.
   */
  deleteActiveRemark(): void {
    if (!this.editable() || !this.isSingleRemark || !this.remarkUuid) return;
    const wasPersisted = !!this.serverUuid(this.remarkUuid);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Delete',
        'Are you sure you want to delete this item?',
      ),
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.removeRemark(this.remarkUuid!);
      this.remarkUuid = null;
      if (!wasPersisted) {
        this.projectsService.isAnyFormDirty.next(false);
        this.close();
        return;
      }
      this.pendingClose = true;
      this.save();
    });
  }

  removeRemark(uuid: string): void {
    if (!this.editable()) return;
    const index = (this.remarks.controls as FormGroup[])
      .findIndex(group => group.get('uuid')?.value === uuid);
    if (index < 0) return;
    this.remarks.removeAt(index);
    this.form.markAsDirty();
  }

  removePicture(remarkUuid: string, pictureId: any): void {
    const remark = this.findRemarkGroup(remarkUuid);
    if (!remark) return;
    const pictures = remark.get('pictures');
    pictures?.setValue((pictures.value || []).filter((p: any) => p.id !== pictureId));
    this.form.markAsDirty();
  }

  triggerUpload(remarkUuid: string): void {
    this.uploadTargetRemarkUuid = remarkUuid;
    this.fileInputRef?.nativeElement?.click();
  }

  /** Same condition as the report editor's `showLocationSection()`. */
  get activeRemarkHasLocation(): boolean {
    const group = this.activeRemarkGroup;
    return !!group && hasRemarkLocation(group.getRawValue());
  }

  /** The location card's "Clear": blank the location controls, and the link with the pin. */
  deleteLocation(): void {
    const group = this.activeRemarkGroup;
    if (!group || !this.editable()) return;
    group.patchValue({ station_range: null });
    clearLocationControls(group);
    this.form.markAsDirty();
    this.projectsService.isAnyFormDirty.next(true);
  }

  /**
   * "+" > "Add location". Same dialog as the report toolbar's
   * `openLocationPickerFromToolbar`: seeded from the remark's own pin when it
   * has one, else the device location, else the site, else the project. The
   * free-text label box is only part of the picker in station-range mode
   * (`enableLocationSection`), as in the report.
   */
  /** W49: "Mark on drawing": the same picker, on its Drawing tab */
  markOnDrawing(): void {
    this.track.track('mark_on_drawing_clicked', { record_type: 'remark' });
    this.openLocationPicker('drawing');
  }

  openLocationPicker(initialTab?: 'drawing'): void {
    const group = this.activeRemarkGroup;
    if (!group || !this.editable()) return;
    const values = group.getRawValue();
    const seed = hasRemarkLocation({ latitude: values.latitude, longitude: values.longitude })
      ? { latitude: values.latitude, longitude: values.longitude }
      : null;

    this.store.select(currentLocation)
      .pipe(take(1))
      .subscribe(location => {
        const fallback = seed || this.fallbackLocation(location);
        this.locationPickerLauncher.open({
          latitude: fallback?.latitude,
          longitude: fallback?.longitude,
          enableLocationSection: this.stationRangeEnabled,
          showIncludeGpsLocationCheckbox: false,
          locationText: values.location || '',
          existingLink: group.get('map_feature_link')?.value ?? null,
          recordLabel: 'remark',
          ...(initialTab ? { initialTab } : {}),
        }).catch(error => console.error('Location picker failed to open', error));
      });
  }

  /**
   * "+" > "Station range". Same dialog config as the report toolbar's
   * `openStationRangeDialog`, pre-filled from the remark's current range so
   * reopening edits rather than starts over. The dialog hands its result to
   * `dailyReportService.selectedLocation` (see ngOnInit).
   */
  openStationRangeDialog(): void {
    const group = this.activeRemarkGroup;
    if (!group || !this.editable() || !this.stationRangeEnabled) return;
    const sr = group.get('station_range')?.value || {};
    const from = sr.from || {};
    const to = sr.to || {};
    this.dialog.open(StationRangeComponent, {
      disableClose: true,
      width: '1200px',
      height: '620px',
      maxWidth: 'calc(100vw - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      panelClass: 'station-range-dialog-panel',
      data: {
        emitSelectedLocation: true,
        form: new FormGroup({
          alignment: new FormControl(sr.alignment || null),
          stationRangeFromStation: new FormControl(from.station || null),
          stationRangeFromOffsetType: new FormControl(from.offsetType ?? from.offset_type ?? null),
          stationRangeFromOffsetDistance: new FormControl(from.offsetDistance ?? from.offset_distance ?? null),
          stationRangeToStation: new FormControl(to.station || null),
          stationRangeToOffsetType: new FormControl(to.offsetType ?? to.offset_type ?? null),
          stationRangeToOffsetDistance: new FormControl(to.offsetDistance ?? to.offset_distance ?? null),
        }),
      },
    });
  }

  /** Picker result -> the active remark. Patches only the keys the dialog sent. */
  private applyPickedLocation(location: any): void {
    const group = this.activeRemarkGroup;
    if (!group || !this.editable()) return;
    const patch = normalizeLocationPayload(location);
    if (!Object.keys(patch).length) return;
    group.patchValue(patch);
    // A pick that carries the link (new, or null after Remove) changed it in this session (R54)
    if ('map_feature_link' in patch) group.get('map_feature_link')?.markAsDirty();
    this.form.markAsDirty();
    this.projectsService.isAnyFormDirty.next(true);
  }

  private resolveStationRangeEnabled(): boolean {
    return resolveDailyReportLocationMode(this.project, this.locationConfig) === 'station_range'
      || projectHasAlignmentGeometry(this.project);
  }

  /** Device location, else the site's pin, else the project's — as the report toolbar. */
  private fallbackLocation(location: any): any {
    if (location?.latitude && location?.longitude) return location;
    let fallback = location || {};
    this.store.select(siteDetails).pipe(take(1)).subscribe(site => {
      if (site?.latitude && site?.longitude) {
        fallback = { ...fallback, latitude: site.latitude, longitude: site.longitude };
      } else if (this.project?.latitude && this.project?.longitude) {
        fallback = { ...fallback, latitude: this.project.latitude, longitude: this.project.longitude };
      }
    });
    return fallback;
  }

  onFileSelected(event: any): void {
    const files: FileList = event.target.files;
    const imageFormData = new FormData();
    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      if (!file) continue;
      imageFormData.append('inputFiles', file);
    }
    if (imageFormData.has('inputFiles')) {
      this.store.dispatch(UploadFileRequest({ payload: imageFormData }));
    }
    if (this.fileInputRef) this.fileInputRef.nativeElement.value = '';
  }

  editable(): boolean {
    return this.mode === 'edit';
  }

  /**
   * Client-side uuids are v4 (36 chars) and exist only to track rows in this
   * form. The api's uuid columns are its own 22-char ids and the schemas
   * validate `Joi.string().length(22)`, so sending a v4 fails the whole save
   * with a 422. Only forward a uuid the SERVER issued; otherwise omit it and
   * let the api mint one (the field is optional).
   */
  private serverUuid(uuid: string): string | null {
    return typeof uuid === 'string' && uuid.length === 22 ? uuid : null;
  }

  buildPayload(): any {
    // A remark link removed this session is { sheet: null }; an untouched null stays a bare null (R54)
    const v = linkRawValue(this.form);
    const diaryUuid = this.serverUuid(this.diary?.uuid);
    return {
      ...(this.diary?.id ? { id: this.diary.id } : (diaryUuid ? { uuid: diaryUuid } : {})),
      // The control holds a moment (shared date picker); the api wants YYYY-MM-DD.
      diary_date: v.diary_date ? moment(v.diary_date).format('YYYY-MM-DD') : null,
      contractor_working: !!v.contractor_working,
      weather: this.buildWeatherPayload(v.weather),
      remarks: (v.remarks || []).map((r: RemarkEditModel) => ({
        ...(this.serverUuid(r.uuid) ? { uuid: this.serverUuid(r.uuid) } : {}),
        ...(r.id ? { id: r.id } : {}),
        type: r.type,
        title: r.title || '',
        description: r.description || '',
        pictures: (r.pictures || [])
          .map((p: any) => p.uuid || p.id)
          .filter(Boolean),
        ...remarkLocationPayload(r),
      })),
    };
  }

  /**
   * The panel edits six fields, but the stored weather holds more: the seed
   * from the weather service also carries `location`, `humidity`, `wind_dir`,
   * `precipitation`, `rainfall_amount_inches` and `condition.code`. The api
   * replaces the whole column (`weather: data.weather ?? diary.weather`), so
   * rebuilding the object from the form alone silently drops the rest —
   * `condition.code` included, which the AASHTOWare sync sends as
   * `RefWeatherId`. Merge onto what is already stored instead.
   */
  buildWeatherPayload(w: any): any {
    const existing = this.diary?.weather || null;
    if (!w) return existing;
    const hasAnyValue = ['avgtemp', 'maxtemp', 'mintemp', 'wind', 'condition_text', 'note']
      .some(k => w[k] !== null && w[k] !== '' && w[k] !== undefined);
    if (!hasAnyValue && !existing) return null;

    const conditionText = w.condition_text ?? null;
    const hasCondition = !!conditionText || !!existing?.condition;

    return {
      ...(existing || {}),
      // `app-number-input` is a text input under the hood, so its control value
      // is a string. The api's weather columns are numeric — coerce here.
      avgtemp: this.asNumber(w.avgtemp),
      maxtemp: this.asNumber(w.maxtemp),
      mintemp: this.asNumber(w.mintemp),
      wind: this.asNumber(w.wind),
      note: w.note,
      // `condition` is `Joi.object()` WITHOUT `.allow(null)`, so sending null
      // 422s the whole save. Omit the key when there is nothing to send, and
      // keep the existing `code` alongside the edited text.
      ...(hasCondition
        ? { condition: { ...(existing?.condition || {}), text: conditionText } }
        : {}),
    };
  }

  private asNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.pendingRemarkIndex = this.isSingleRemark ? this.activeRemarkIndex : -1;
    const payload = this.buildPayload();

    this.projectsService.saveDailyDiaryDraft({
      siteId: this.data?.siteId,
      data: payload,
      // The version this form loaded (or last saved; `this.diary` is replaced by the save
      // response below). The api 409s if the diary changed since. Absent on a create.
      baseUpdatedAt: baseUpdatedAtFor(this.diary),
    }).pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (resp: any) => {
          void this.sheetMarkups.flush(resp);
          const node = resp?.data || resp;
          const saved = node?.attributes ? { id: node.id, ...node.attributes } : node;
          this.diary = saved;
          this.diaryId = saved?.id || saved?.uuid || this.diaryId;
          this.mode = 'edit';
          this.applyAbilityGates(saved);
          this.patchFormFromDiary(saved);

          this.saving = false;
          this.commonService.notification('Diary saved', 'success');
          this.projectsService.dailyDiaryChanged.next();
          this.projectsService.isAnyFormDirty.next(false);
          if (this.pendingClose) {
            this.pendingClose = false;
            this.close();
          }
        },
        error: (err: any) => {
          this.saving = false;
          this.pendingClose = false;
          const conflict = reportChangedBody(err);
          if (conflict) {
            this.onDiaryChanged(conflict);
            return;
          }
          this.commonService.errorNotification(err?.error?.error || 'Failed to save diary');
        },
      });
  }

  /**
   * 409 report_changed: the diary changed since this form loaded it (e.g. on the mobile app)
   * and nothing was written. No error toast. Refresh reloads the diary into the form; Cancel
   * keeps the form as is, and the next save 409s again until the user refreshes.
   */
  private onDiaryChanged(conflict: ReportChangedBody): void {
    openReportChangedDialog(this.dialog, conflict, this.authService.getCurrentUser(), 'diary')
      .pipe(takeUntil(this.onDestroy))
      .subscribe(refresh => {
        if (!refresh) return;
        // The failed save's index fallback (resolveActiveRemark) must not land an unsaved
        // new remark on whatever remark now sits at that position in the server copy.
        this.pendingRemarkIndex = -1;
        const diaryId = this.diary?.id || this.diary?.uuid || this.diaryId;
        if (diaryId) {
          this.loadDiary(diaryId);
        }
        // The day view lists this diary too; let it pick up the other change.
        this.projectsService.dailyDiaryChanged.next();
      });
  }



  close(): void {
    this.projectsService.closeRightPanel.next();
  }


  ngOnDestroy(): void {
    // The flag is shared with the report's panels; leaving it true after this
    // panel closes would block the next navigation with a phantom prompt.
    this.projectsService.isAnyFormDirty.next(false);
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
